import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  Coffee,
  Lightbulb,
  Layout,
  Code2,
  Search,
  Rocket,
  ShieldCheck,
  ChevronRight,
  ChevronDown,
  Palette,
  Monitor,
  SearchCheck,
  Smartphone,
  Settings,
  Lock,
  Zap
} from 'lucide-react';
import SEO from '../components/SEO';
import './Diensten.css';

const Diensten = () => {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(null);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.15
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  const headerVariants = {
    hidden: { opacity: 0, y: -40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  const steps = [
    {
      title: t('diensten.step_1_title'),
      description: t('diensten.step_1_desc'),
      icon: <Coffee className="w-6 h-6" />,
      details: [
        t('diensten.step_1_detail_1', 'Vrijblijvend intakegesprek'),
        t('diensten.step_1_detail_2', 'Doelstellingen bepalen'),
        t('diensten.step_1_detail_3', 'Offerte op maat')
      ],
      color: "bg-orange-500"
    },
    {
      title: t('diensten.step_2_title'),
      description: t('diensten.step_2_desc'),
      icon: <Lightbulb className="w-6 h-6" />,
      details: [
        t('diensten.step_2_detail_1', 'Sitemap opstellen'),
        t('diensten.step_2_detail_2', 'Wireframes (schetsen)'),
        t('diensten.step_2_detail_3', 'Content strategie')
      ],
      color: "bg-orange-600"
    },
    {
      title: t('diensten.step_3_title'),
      description: t('diensten.step_3_desc'),
      icon: <Layout className="w-6 h-6" />,
      details: [
        t('diensten.step_3_detail_1', 'Visueel UI ontwerp'),
        t('diensten.step_3_detail_2', 'Responsive design (mobiel/tablet)'),
        t('diensten.step_3_detail_3', 'Feedbackronde')
      ],
      color: "bg-orange-700"
    },
    {
      title: t('diensten.step_4_title'),
      description: t('diensten.step_4_desc'),
      icon: <Code2 className="w-6 h-6" />,
      details: [
        t('diensten.step_4_detail_1', 'Clean coding'),
        t('diensten.step_4_detail_2', 'CMS integratie'),
        t('diensten.step_4_detail_3', 'Interactieve elementen')
      ],
      color: "bg-orange-600"
    },
    {
      title: t('diensten.step_5_title'),
      description: t('diensten.step_5_desc'),
      icon: <Search className="w-6 h-6" />,
      details: [
        t('diensten.step_5_detail_1', 'Cross-browser checks'),
        t('diensten.step_5_detail_2', 'Snelheidsoptimalisatie'),
        t('diensten.step_5_detail_3', 'Laatste puntjes op de i')
      ],
      color: "bg-orange-500"
    },
    {
      title: t('diensten.step_6_title'),
      description: t('diensten.step_6_desc'),
      icon: <Rocket className="w-6 h-6" />,
      details: [
        t('diensten.step_6_detail_1', 'Livegang'),
        t('diensten.step_6_detail_2', 'Google Search Console koppeling'),
        t('diensten.step_6_detail_3', 'Basis SEO check')
      ],
      color: "bg-orange-400"
    },
    {
      title: t('diensten.step_7_title'),
      description: t('diensten.step_7_desc'),
      icon: <ShieldCheck className="w-6 h-6" />,
      details: [
        t('diensten.step_7_detail_1', 'Maandelijkse updates'),
        t('diensten.step_7_detail_2', 'Beveiliging & backups'),
        t('diensten.step_7_detail_3', 'Vragen & ondersteuning')
      ],
      color: "bg-orange-500"
    }
  ];

  const services = [
    {
      icon: <Palette className="w-8 h-8" />,
      title: t('diensten.service_design_title'),
      description: t('diensten.service_design_desc'),
      features: [
        t('diensten.service_design_feat_1', 'Op maat gemaakt design'),
        t('diensten.service_design_feat_2', 'Responsive voor alle apparaten'),
        t('diensten.service_design_feat_3', 'User Experience optimalisatie'),
        t('diensten.service_design_feat_4', 'Merkidentiteit integratie')
      ]
    },
    {
      icon: <Monitor className="w-8 h-8" />,
      title: t('diensten.service_dev_title'),
      description: t('diensten.service_dev_desc'),
      features: [
        t('diensten.service_dev_feat_1', 'React & Next.js ontwikkeling'),
        t('diensten.service_dev_feat_2', 'Snelle laadtijden'),
        t('diensten.service_dev_feat_3', 'Clean & onderhoudbare code'),
        t('diensten.service_dev_feat_4', 'API integraties')
      ]
    },
    {
      icon: <SearchCheck className="w-8 h-8" />,
      title: t('diensten.service_seo_title'),
      description: t('diensten.service_seo_desc'),
      features: [
        t('diensten.service_seo_feat_1', 'On-page SEO'),
        t('diensten.service_seo_feat_2', 'Technische SEO'),
        t('diensten.service_seo_feat_3', 'Keyword research'),
        t('diensten.service_seo_feat_4', 'Google Analytics setup')
      ]
    },
    {
      icon: <Smartphone className="w-8 h-8" />,
      title: t('diensten.service_responsive_title'),
      description: t('diensten.service_responsive_desc'),
      features: [
        t('diensten.service_responsive_feat_1', 'Mobile-first approach'),
        t('diensten.service_responsive_feat_2', 'Touch-optimized'),
        t('diensten.service_responsive_feat_3', 'Cross-browser compatible'),
        t('diensten.service_responsive_feat_4', 'Retina ready')
      ]
    },
    {
      icon: <Settings className="w-8 h-8" />,
      title: t('diensten.service_cms_title'),
      description: t('diensten.service_cms_desc'),
      features: [
        t('diensten.service_cms_feat_1', 'Strapi CMS setup'),
        t('diensten.service_cms_feat_2', 'Content beheer training'),
        t('diensten.service_cms_feat_3', 'Custom content types'),
        t('diensten.service_cms_feat_4', 'Media library')
      ]
    },
    {
      icon: <Lock className="w-8 h-8" />,
      title: t('diensten.service_support_title'),
      description: t('diensten.service_support_desc'),
      features: [
        t('diensten.service_support_feat_1', 'Beveiligingsupdates'),
        t('diensten.service_support_feat_2', 'Performance monitoring'),
        t('diensten.service_support_feat_3', 'Bug fixes'),
        t('diensten.service_support_feat_4', 'Technische support')
      ]
    }
  ];

  return (
    <div className="diensten">
      <SEO 
        title={t('diensten.hero_title').replace('\n', ' ')}
        description={t('diensten.hero_subtitle')}
        canonical="/diensten"
      />
      {/* Hero Section */}
      <section className="diensten-hero">
        <div className="container">
          <div className="hero-grid">
            <motion.div 
              className="hero-content"
              initial="hidden"
              animate="visible"
              variants={headerVariants}
            >
              <h1 className="page-title">
                {t('diensten.hero_title').split('\n')[0]}<br />
                <span className="text-gradient">{t('diensten.hero_title').split('\n')[1]}</span>
              </h1>
              <p className="page-subtitle">
                {t('diensten.hero_subtitle')}
              </p>
              <div className="hero-buttons">
                <Link to="/contact" className="btn btn-primary">
                  {t('diensten.cta_start')}
                </Link>
                <Link to="/portfolio" className="btn btn-secondary">
                  {t('diensten.cta_portfolio')}
                </Link>
              </div>
            </motion.div>

            <motion.div 
              className="hero-visual"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
            >
              {/* Abstract Services Illustration - Modular/Service Cards */}
              <svg viewBox="0 0 600 500" className="hero-svg">
                <defs>
                  <linearGradient id="heroGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#fff7ed" />
                    <stop offset="100%" stopColor="#ffedd5" />
                  </linearGradient>
                  <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
                    <feDropShadow dx="0" dy="10" stdDeviation="10" floodColor="#000000" floodOpacity="0.1" />
                  </filter>
                </defs>

                <circle cx="300" cy="250" r="220" fill="url(#heroGrad)" opacity="0.6" />
                
                {/* Central Hub - Strategy/Core */}
                <motion.g
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <circle cx="300" cy="250" r="60" fill="white" filter="url(#shadow)" />
                  <circle cx="300" cy="250" r="50" fill="#f97316" opacity="0.1" />
                  {/* Abstract Logo Shape */}
                  <path d="M285 250 L315 250 M300 235 L300 265" stroke="#f97316" strokeWidth="4" strokeLinecap="round" />
                  <circle cx="300" cy="250" r="8" fill="#f97316" />
                </motion.g>

                {/* Floating Card 1: Design (Left) */}
                <motion.g
                  animate={{ y: [-10, 10, -10] }}
                  transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                >
                  <rect x="60" y="180" width="150" height="110" rx="12" fill="white" filter="url(#shadow)" />
                  {/* Palette UI */}
                  <circle cx="90" cy="210" r="12" fill="#ef4444" />
                  <circle cx="120" cy="210" r="12" fill="#fbbf24" />
                  <circle cx="150" cy="210" r="12" fill="#3b82f6" />
                  <rect x="80" y="240" width="110" height="6" rx="3" fill="#e2e8f0" />
                  <rect x="80" y="255" width="80" height="6" rx="3" fill="#e2e8f0" />
                  
                  {/* Connector Line */}
                  <motion.path 
                    d="M210 235 L250 245" 
                    stroke="#f97316" strokeWidth="2" strokeDasharray="4 4" opacity="0.4" 
                    animate={{ opacity: [0.2, 0.6, 0.2] }}
                    transition={{ duration: 3, repeat: Infinity }}
                  />
                </motion.g>

                {/* Floating Card 2: Development (Right) */}
                <motion.g
                  animate={{ y: [10, -10, 10] }}
                  transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                >
                  <rect x="390" y="150" width="150" height="110" rx="12" fill="white" filter="url(#shadow)" />
                  {/* Code UI */}
                  <text x="410" y="195" fontFamily="monospace" fontSize="24" fill="#f97316" fontWeight="bold">&lt;/&gt;</text>
                  <rect x="410" y="215" width="110" height="6" rx="3" fill="#e2e8f0" />
                  <rect x="410" y="230" width="90" height="6" rx="3" fill="#e2e8f0" />

                  {/* Connector Line */}
                  <motion.path 
                    d="M390 205 L350 230" 
                    stroke="#f97316" strokeWidth="2" strokeDasharray="4 4" opacity="0.4"
                    animate={{ opacity: [0.2, 0.6, 0.2] }}
                    transition={{ duration: 3, repeat: Infinity, delay: 1 }}
                  />
                </motion.g>

                {/* Floating Card 3: SEO/Growth (Bottom) */}
                <motion.g
                  animate={{ y: [-5, 5, -5] }}
                  transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                >
                  <rect x="225" y="340" width="150" height="110" rx="12" fill="white" filter="url(#shadow)" />
                  {/* Chart UI */}
                  <rect x="245" y="400" width="20" height="30" rx="2" fill="#e2e8f0" />
                  <rect x="275" y="380" width="20" height="50" rx="2" fill="#fb923c" />
                  <rect x="305" y="360" width="20" height="70" rx="2" fill="#f97316" />
                  <path d="M335 365 L350 350 L360 360" stroke="#10b981" strokeWidth="3" strokeLinecap="round" fill="none" />

                  {/* Connector Line */}
                  <motion.path 
                    d="M300 340 L300 300" 
                    stroke="#f97316" strokeWidth="2" strokeDasharray="4 4" opacity="0.4"
                    animate={{ opacity: [0.2, 0.6, 0.2] }}
                    transition={{ duration: 3, repeat: Infinity, delay: 0.5 }}
                  />
                </motion.g>

                {/* Floating Particles */}
                <motion.circle cx="150" cy="100" r="6" fill="#f97316" opacity="0.4" 
                  animate={{ y: [0, -20, 0], opacity: [0.4, 0.8, 0.4] }}
                  transition={{ duration: 4, repeat: Infinity }}
                />
                <motion.circle cx="480" cy="350" r="8" fill="#3b82f6" opacity="0.3" 
                  animate={{ y: [0, 20, 0], opacity: [0.3, 0.6, 0.3] }}
                  transition={{ duration: 5, repeat: Infinity, delay: 1 }}
                />
                <motion.circle cx="500" cy="100" r="5" fill="#10b981" opacity="0.4" 
                  animate={{ x: [0, 10, 0], opacity: [0.4, 0.7, 0.4] }}
                  transition={{ duration: 6, repeat: Infinity, delay: 2 }}
                />

              </svg>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="services-section section">
        <div className="container">
          <motion.div 
            className="services-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            {services.map((service, index) => (
              <motion.div 
                key={index} 
                className="service-card card"
                variants={itemVariants}
                whileHover={{ 
                  scale: 1.03,
                  transition: { duration: 0.2 }
                }}
              >
                <motion.div 
                  className="service-icon"
                  whileHover={{ rotate: [0, -10, 10, -10, 0] }}
                  transition={{ duration: 0.5 }}
                >
                  {service.icon}
                </motion.div>
                <h3 className="service-title">{service.title}</h3>
                <p className="service-description">{service.description}</p>
                <ul className="service-features">
                  {service.features.map((feature, idx) => (
                    <motion.li 
                      key={idx}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      viewport={{ once: true }}
                    >
                      <span className="check-icon">✓</span>
                      {feature}
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Quality/Process Visual Section */}
      <section className="dark-section section">
        <div className="container">
          <div className="dark-grid">
            <motion.div 
              className="dark-content"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2>{t('diensten.quality_title')}</h2>
              <p>
                {t('diensten.quality_desc')}
              </p>
              <ul className="dark-features">
                <li>
                  <span className="feature-icon"><Zap size={24} color="#f97316" /></span>
                  <div>
                    <strong>{t('diensten.feature_fast')}</strong>
                    <span>{t('diensten.feature_fast_desc')}</span>
                  </div>
                </li>
                <li>
                  <span className="feature-icon"><ShieldCheck size={24} color="#f97316" /></span>
                  <div>
                    <strong>{t('diensten.feature_secure')}</strong>
                    <span>{t('diensten.feature_secure_desc')}</span>
                  </div>
                </li>
                <li>
                  <span className="feature-icon"><Smartphone size={24} color="#f97316" /></span>
                  <div>
                    <strong>{t('diensten.feature_responsive')}</strong>
                    <span>{t('diensten.feature_responsive_desc')}</span>
                  </div>
                </li>
              </ul>
            </motion.div>

            <motion.div 
              className="dark-illustration"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <svg viewBox="0 0 500 400" className="quality-svg">
                <defs>
                  <linearGradient id="layerGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#1e293b" stopOpacity="0.8" />
                    <stop offset="100%" stopColor="#0f172a" stopOpacity="0.9" />
                  </linearGradient>
                  <filter id="glow-quality" x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur stdDeviation="5" result="blur" />
                    <feComposite in="SourceGraphic" in2="blur" operator="over" />
                  </filter>
                </defs>

                {/* Abstract Background Glow */}
                <circle cx="250" cy="200" r="150" fill="#f97316" opacity="0.05" filter="url(#glow-quality)" />

                {/* Layer 1: Infrastructure (Bottom) */}
                <motion.g
                  initial={{ y: 20, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                >
                  <path d="M150 240 L250 290 L350 240 L250 190 Z" fill="#1e293b" stroke="#334155" strokeWidth="2" />
                  <path d="M150 240 L150 260 L250 310 L350 260 L350 240" fill="none" stroke="#334155" strokeWidth="2" />
                </motion.g>

                {/* Layer 2: Code/Logic (Middle) */}
                <motion.g
                  initial={{ y: 20, opacity: 0 }}
                  whileInView={{ y: -30, opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                >
                  <path d="M150 210 L250 260 L350 210 L250 160 Z" fill="#1e293b" stroke="#3b82f6" strokeWidth="2" />
                  {/* Code lines */}
                  <path d="M220 210 L280 210" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" />
                  <path d="M200 225 L260 225" stroke="#334155" strokeWidth="2" strokeLinecap="round" />
                  <path d="M240 195 L300 195" stroke="#334155" strokeWidth="2" strokeLinecap="round" />
                </motion.g>

                {/* Layer 3: UI/Design (Top) */}
                <motion.g
                  initial={{ y: 20, opacity: 0 }}
                  whileInView={{ y: -60, opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.5 }}
                >
                  <path d="M150 180 L250 230 L350 180 L250 130 Z" fill="url(#layerGrad)" stroke="#f97316" strokeWidth="2" />
                  <circle cx="250" cy="180" r="15" fill="#f97316" />
                  <path d="M190 190 L220 205" stroke="#f97316" strokeWidth="2" opacity="0.5" />
                  <path d="M280 165 L310 180" stroke="#f97316" strokeWidth="2" opacity="0.5" />
                </motion.g>

                {/* Connecting Beam/Scan */}
                <motion.path
                  d="M250 130 L250 310"
                  stroke="url(#layerGrad)"
                  strokeWidth="1"
                  strokeDasharray="4 4"
                  initial={{ pathLength: 0, opacity: 0 }}
                  whileInView={{ pathLength: 1, opacity: 0.3 }}
                  transition={{ duration: 1, delay: 0.8 }}
                />
                
                {/* Floating Particles */}
                <motion.circle cx="350" cy="150" r="4" fill="#3b82f6" 
                  animate={{ y: [0, -10, 0] }} transition={{ duration: 3, repeat: Infinity }} />
                <motion.circle cx="150" cy="280" r="3" fill="#f97316" 
                  animate={{ y: [0, 10, 0] }} transition={{ duration: 4, repeat: Infinity }} />

              </svg>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Werkwijze Section */}
      <section className="werkwijze-section section">
        <div className="container">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="section-title">
              {t('diensten.process_title').split(' ').slice(0, 1)} <span style={{ color: '#f97316' }}>{t('diensten.process_title').split(' ').slice(1)}</span>
            </h2>
            <p className="section-subtitle">
              {t('diensten.process_subtitle')}
            </p>
          </motion.div>

          <div className="relative">
            {/* Vertical line for desktop */}
            <div className="werkwijze-timeline-line"></div>

            <div className="werkwijze-steps">
              {steps.map((step, index) => (
                <motion.div 
                  key={index} 
                  className={`werkwijze-step-wrapper ${index % 2 === 0 ? 'reverse' : ''}`}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ 
                    duration: 0.5, 
                    delay: index * 0.1,
                    ease: 'easeOut'
                  }}
                >
                  
                  {/* Connector Dot */}
                  <div className="werkwijze-timeline-dot-wrapper">
                    <motion.div 
                      className={`werkwijze-timeline-dot ${step.color}`}
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ 
                        duration: 0.4, 
                        delay: index * 0.1 + 0.2,
                        type: 'spring',
                        stiffness: 200
                      }}
                      whileHover={{ scale: 1.1 }}
                    >
                      {step.icon}
                    </motion.div>
                  </div>

                  {/* Content Card */}
                  <div className="werkwijze-step-content">
                    <motion.div
                      className={`werkwijze-card ${activeStep === index ? 'active' : ''}`}
                      onClick={() => setActiveStep(activeStep === index ? null : index)}
                      whileHover={{ scale: 1.02 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="werkwijze-card-header">
                        <div className={`werkwijze-icon-mobile ${step.color}`}>
                          {step.icon}
                        </div>
                        <span className="werkwijze-step-label">Stap {index + 1}</span>
                      </div>
                      
                      <h3 className="werkwijze-title">{step.title}</h3>
                      <p className="werkwijze-description">
                        {step.description}
                      </p>

                      <button className="werkwijze-toggle">
                        {activeStep === index ? t('diensten.less_info') : t('diensten.more_info')}
                        {activeStep === index ? 
                          <ChevronDown className="ml-1 w-4 h-4" /> : 
                          <ChevronRight className="ml-1 w-4 h-4" />
                        }
                      </button>

                      {activeStep === index && (
                        <motion.ul 
                          className="werkwijze-details"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          {step.details.map((detail, dIdx) => (
                            <motion.li 
                              key={dIdx}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: dIdx * 0.1 }}
                            >
                              <div className={`werkwijze-detail-dot ${step.color}`}></div>
                              {detail}
                            </motion.li>
                          ))}
                        </motion.ul>
                      )}
                    </motion.div>
                  </div>

                  {/* Spacer for the other side on desktop */}
                  <div className="werkwijze-spacer"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="diensten-cta section">
        <div className="container">
          <motion.div 
            className="cta-box"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="cta-bg-pattern">
              <div className="cta-circle cta-circle-1"></div>
              <div className="cta-circle cta-circle-2"></div>
            </div>
            
            <div className="cta-content">
              <h2>{t('diensten.cta_footer_title')}</h2>
              <p>
                {t('diensten.cta_footer_text')}
              </p>
              <div className="cta-buttons">
                <Link to="/contact" className="btn btn-primary">
                  {t('diensten.cta_footer_btn_primary')}
                </Link>
                <Link to="/portfolio" className="btn btn-secondary">
                  {t('diensten.cta_footer_btn_secondary')}
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Diensten;
